import React, { useEffect, useRef } from 'react';

/* ── DOM-driven animated score number ── */
const ScoreNumber = ({ initScore, busKey, bus, accent }) => {
  const numRef = useRef(null);
  const glowRef = useRef(null);
  const prevRef = useRef(initScore);

  useEffect(() => {
    if (numRef.current) numRef.current.textContent = initScore;
    prevRef.current = initScore;

    const unsub = bus.on(busKey, score => {
      if (numRef.current) numRef.current.textContent = score;
      if (score > prevRef.current && glowRef.current) {
        glowRef.current.classList.remove('score-flash');
        void glowRef.current.offsetWidth;
        glowRef.current.classList.add('score-flash');
      }
      prevRef.current = score;
    });

    return unsub;
  }, [busKey, bus]);

  return (
    <div className="relative flex items-center justify-center w-full">
      <div
        ref={glowRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0,
          background: `radial-gradient(ellipse at center, ${accent}80 0%, transparent 65%)`
        }}
      />
      {/* 
        數字大小優化：
        將字體最大值從 42vw 稍微降到 36vw，避免在超寬螢幕上把上下空間吃滿。
        lineHeight 調整為 0.9，給予數字適當的行高避免切換時跳動過大。
      */}
      <span
        ref={numRef}
        className="tabular-nums select-none"
        style={{
          display: 'block',
          textAlign: 'center',
          fontSize: 'clamp(100px, min(36vw, 60vh), 750px)',
          lineHeight: 0.9,
          fontWeight: 900,
          letterSpacing: '-0.04em'
        }}
      >
        {initScore}
      </span>
    </div>
  );
};

/* ── Game dots (加大加粗版) ── */
const GameDots = React.memo(({ games, gamesNeeded, accent }) => (
  <div className="flex gap-2.5 justify-center">
    {Array.from({ length: gamesNeeded }).map((_, i) => (
      <div
        key={i}
        className="rounded-full transition-all duration-500"
        style={{
          width: 'clamp(10px, 2.8vw, 18px)',
          height: 'clamp(8px, 1.4vw, 12px)',
          backgroundColor: i < games ? accent : 'rgba(255,255,255,0.12)',
          boxShadow: i < games ? `0 0 8px ${accent}` : 'none',
        }}
      />
    ))}
  </div>
));

/* ── Serving Badge (發球圖標 — 超醒目版) ── */
const ServingBadge = ({ isServing, serveSide, accent }) => {
  if (!isServing) return null;

  return (
    <div
      className="flex items-center justify-center gap-2 px-4 py-2 rounded-full border-2 animate-pulse shadow-lg"
      style={{
        backgroundColor: `${accent}25`,
        borderColor: `${accent}70`,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div
        className="relative flex items-center justify-center"
        style={{ width: 'clamp(24px, 4vw, 36px)', height: 'clamp(24px, 4vw, 36px)' }}
      >
        <div className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: accent, opacity: 0.4 }} />
        <span className="relative text-2xl md:text-3xl">🏸</span>
      </div>
      <span
        className="font-black tracking-wide"
        style={{ fontSize: 'clamp(0.75rem, 2vw, 1.1rem)', color: accent }}
      >
        {serveSide === 'right' ? '右區發球' : '左區發球'}
      </span>
    </div>
  );
};

const ScorePanel = React.memo(({
  side, teamId, name, subName, isServing, serveSide, games, gamesNeeded, initScore, busKey, bus, onTap, matchPointAlert,
}) => {
  const isLeft = side === 'left';
  const accent = teamId === 'A' ? '#818cf8' : '#fb7185';
  const gradientDir = isLeft ? '90deg' : '270deg';
  const glowPos = isLeft ? '15% 50%' : '85% 50%';

  return (
    <div className="tap-zone relative flex-1 min-w-0 flex flex-col overflow-hidden" onClick={onTap}>
      {/* Side tint */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: `linear-gradient(${gradientDir}, ${accent}16 0%, transparent 100%)` }}
      />
      {/* Serving glow */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{
          opacity: isServing ? 1 : 0,
          background: `radial-gradient(ellipse at ${glowPos}, ${accent}26 0%, transparent 60%)`
        }}
      />

      {/* ── TOP SECTION (隊伍資訊區) - z-index 提高到 20 ── */}
      <div
        className="shrink-0 flex flex-col items-center z-20 w-full px-3 relative"
        style={{ paddingTop: 'clamp(12px, 2.5vh, 24px)', gap: 'clamp(6px, 1.2vh, 12px)' }}
      >
        {/* 發球指示條 */}
        <div
          className="rounded-full transition-all duration-300"
          style={{
            width: 'clamp(24px, 5vw, 48px)',
            height: 'clamp(4px, 0.8vh, 6px)',
            backgroundColor: accent,
            opacity: isServing ? 1 : 0.2,
          }}
        />

        {/* 隊伍名稱 — 超大版 */}
        <span
          className="font-black tracking-wide text-center w-full transition-all duration-300"
          style={{
            fontSize: 'clamp(1rem, 3.5vw, 1.5rem)',
            color: isServing ? '#ffffff' : 'rgba(255,255,255,0.3)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textShadow: isServing ? `0 0 12px ${accent}60` : '0 2px 4px rgba(0,0,0,0.8)',
          }}
        >
          {name}
        </span>

        {/* 球員名稱 — 放大版 */}
        {subName && (
          <span
            className="font-bold text-center w-full"
            style={{
              fontSize: 'clamp(0.65rem, 2vw, 0.95rem)',
              color: isServing ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.25)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textShadow: '0 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            {subName}
          </span>
        )}

        {/* 局數指示點 */}
        <GameDots games={games} gamesNeeded={gamesNeeded} accent={accent} />

        {/* 賽末分警示 (移動到這裡，不與大分數搶空間) */}
        {matchPointAlert && (
          <div
            className="mt-1 px-4 py-1.5 rounded-full font-black uppercase tracking-widest animate-pulse shadow-xl"
            style={{
              fontSize: 'clamp(0.65rem, 2vw, 0.9rem)',
              backgroundColor: `${accent}28`,
              border: `2px solid ${accent}70`,
              color: accent,
              whiteSpace: 'nowrap',
              backdropFilter: 'blur(8px)',
            }}
          >
            ⚡ {matchPointAlert}
          </div>
        )}
      </div>

      {/* ── MIDDLE SECTION (分數區 — 核心) z-index 降到 10 ── */}
      <div className="flex-1 flex flex-col items-center justify-center z-10 w-full px-2" style={{ minHeight: 0 }}>
        {/* 超大分數 */}
        <div
          className="w-full flex items-center justify-center"
          style={{ color: isServing ? '#ffffff' : 'rgba(255,255,255,0.15)' }}
        >
          <ScoreNumber initScore={initScore} busKey={busKey} bus={bus} accent={accent} />
        </div>
      </div>

      {/* ── BOTTOM SECTION (操作提示區) z-index 提高到 20 ── */}
      <div
        className="shrink-0 flex flex-col items-center z-20 w-full px-3 relative"
        style={{ paddingBottom: 'clamp(12px, 2.5vh, 24px)', gap: 'clamp(8px, 1.5vh, 16px)' }}
      >
        {/* 發球區徽章 */}
        <ServingBadge isServing={isServing} serveSide={serveSide} accent={accent} />

        {/* 點擊提示 */}
        <div className="flex items-center gap-2 opacity-60 hover:opacity-90 transition-opacity">
          <span
            className="font-black tracking-wider"
            style={{ fontSize: 'clamp(0.7rem, 2vw, 1rem)', color: 'rgba(255,255,255,0.5)', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
          >
            {isLeft ? '← 點此得分' : '點此得分 →'}
          </span>
        </div>
      </div>
    </div>
  );
}, (prev, next) =>
  prev.side === next.side &&
  prev.teamId === next.teamId &&
  prev.name === next.name &&
  prev.subName === next.subName &&
  prev.isServing === next.isServing &&
  prev.serveSide === next.serveSide &&
  prev.games === next.games &&
  prev.gamesNeeded === next.gamesNeeded &&
  prev.busKey === next.busKey &&
  prev.matchPointAlert === next.matchPointAlert &&
  prev.onTap === next.onTap
);

export default ScorePanel;